import React, { Suspense, useEffect, useMemo } from "react"
import { useLocation, useMatches } from "@tanstack/react-router"
import { FloatingAIPill } from "./FloatingAIPill"
import { WelcomeTooltip } from "./WelcomeTooltip"
import { resolveCopilotContext } from "@/lib/copilot/context"
import { useCopilotStore } from "@/store/copilot.store"
import { useAuthStore } from "@/store/auth.store"
import { useBuyerUIStore } from "@/store/buyer-ui.store"
import { useSellerUIStore } from "@/store/seller-ui.store"
import { useAdminUIStore } from "@/store/admin-ui.store"

// Lazy load the chat drawer UI
const LazyChatPanel = React.lazy(() => import("./ChatPanel"))

export function EngaAICopilot() {
  const isOpen = useCopilotStore((s) => s.isOpen)
  const setLastVisitPath = useCopilotStore((s) => s.setLastVisitPath)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const role = useAuthStore((s) => s.user?.role)
  const location = useLocation()
  const matches = useMatches()
  const buyerActiveNav = useBuyerUIStore((s) => s.activeNav)
  const sellerActiveNav = useSellerUIStore((s) => s.activeNav)
  const adminActiveNav = useAdminUIStore((s) => s.activeNav)

  const context = useMemo(
    () =>
      resolveCopilotContext(
        [
          {
            type: "url",
            pathname: location.pathname,
            search: location.search as Record<string, string>,
          },
        ],
        { authenticated: isAuthenticated }
      ),
    [location.pathname, location.search, isAuthenticated]
  )

  // Track path switches
  useEffect(() => {
    setLastVisitPath(location.pathname)
  }, [location.pathname, setLastVisitPath])

  // Hide chatbot on non-existing routes (404 Not Found)
  const lastMatch = matches[matches.length - 1]
  const isNotFoundRoute =
    !lastMatch ||
    lastMatch.routeId === "__root__" ||
    (lastMatch as any).globalNotFound ||
    matches.some((m: any) => m.globalNotFound || m.status === "notFound")

  if (isNotFoundRoute) return null

  // The full-page /chat view replaces the floating widget.
  if (location.pathname.startsWith("/chat")) return null

  // Hide chatbot on complaints routes and dashboard complaints tab
  const pathnameLower = (location.pathname || "").toLowerCase()
  const searchLower = (location.searchStr || JSON.stringify(location.search || {})).toLowerCase()

  const isComplaintsRoute =
    pathnameLower.includes("complaint") ||
    searchLower.includes("complaint") ||
    buyerActiveNav === "complaints" ||
    sellerActiveNav === "complaints" ||
    adminActiveNav === "complaints"

  if (isComplaintsRoute) return null

  // Hide chatbot on notifications routes and dashboard notifications tab
  const isNotificationsRoute =
    pathnameLower.includes("notification") ||
    searchLower.includes("notification") ||
    buyerActiveNav === "notifications" ||
    sellerActiveNav === "notifications" ||
    adminActiveNav === "notifications"

  if (isNotificationsRoute) return null

  // Hide chatbot on auth pages, profile setup, and company setup
  const isAuthOrProfileRoute =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/signup") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/buyer-profile-setup") ||
    location.pathname.startsWith("/buyer-profile") ||
    location.pathname.startsWith("/buyer/profile") ||
    location.pathname.startsWith("/seller-profile-setup") ||
    location.pathname.startsWith("/seller-company-setup") ||
    location.pathname.startsWith("/seller-profile") ||
    location.pathname.startsWith("/seller/profile") ||
    location.pathname.startsWith("/profile") ||
    buyerActiveNav === "profile" ||
    sellerActiveNav === "profile"

  if (isAuthOrProfileRoute) return null

  return (
    <>
      {/* Tiny trigger assets */}
      <FloatingAIPill />
      <WelcomeTooltip />

      {/* Lazy bundle mounts conditionally with loading fallback */}
      {isOpen && (
        <Suspense fallback={null}>
          <LazyChatPanel
            context={context}
            pathname={location.pathname}
            search={location.search as Record<string, string>}
          />
        </Suspense>
      )}
    </>
  )
}